import { getCurrentIdToken } from "../[id]/profil/fotos/onboarding/photoOnboardingTracking.js";

export function getFrontendSessionCookie(cookieString) {
  const source =
    typeof cookieString === "string"
      ? cookieString
      : typeof document !== "undefined"
        ? document.cookie
        : "";
  const match = source
    .split(";")
    .map((part) => part.trim())
    .find((part) => part.startsWith("__session_front="));
  return match ? match.slice("__session_front=".length) : null;
}

/**
 * Adds both supported customer-auth mechanisms to a request.
 *
 * The token provider is called for every invocation, so persistent queues get
 * the current Firebase ID token on every retry. The session cookie remains a
 * fallback for privacy modes or temporarily unavailable Firebase Auth.
 */
export async function withCustomerRequestAuth(
  requestOptions = {},
  { getIdToken = getCurrentIdToken, forceRefresh = false } = {}
) {
  let idToken = null;
  try {
    idToken = (await getIdToken?.(forceRefresh)) || null;
  } catch {
    idToken = null;
  }

  const headers = new Headers(requestOptions.headers || {});
  if (idToken) {
    headers.set("Authorization", `Bearer ${idToken}`);
  }
  // The API runs on a cross-site run.app origin, so its SameSite=Lax cookie is
  // not a dependable fallback. Send the frontend-domain session alongside a
  // bearer token as well: if a cached token has just expired, legacy-compatible
  // routes may answer 200 without triggering the 401 refresh path. The backend
  // independently verifies this signed Firebase session before trusting it.
  const frontendSession = getFrontendSessionCookie();
  if (frontendSession) headers.set("X-AEL-Session", frontendSession);

  return {
    ...requestOptions,
    headers,
    credentials: "include",
  };
}

/**
 * Retries an authenticated request exactly once after a 401 with a forced
 * Firebase token refresh. Multipart FormData and JSON bodies used by the
 * upload/delete flows are replayable, and auth runs on the backend before
 * Multer, so the first rejected request cannot partially commit an upload.
 */
export async function fetchWithCustomerRequestAuth(
  url,
  requestOptions = {},
  {
    fetchImpl = globalThis.fetch,
    getIdToken = getCurrentIdToken,
  } = {}
) {
  if (typeof fetchImpl !== "function") {
    throw new TypeError("fetch ist für diesen Request nicht verfügbar.");
  }

  const firstResponse = await fetchImpl(
    url,
    await withCustomerRequestAuth(requestOptions, { getIdToken })
  );
  if (firstResponse?.status !== 401) return firstResponse;
  try {
    await firstResponse.body?.cancel();
  } catch {
    // The forced-refresh retry does not depend on the small 401 response body.
  }

  return fetchImpl(
    url,
    await withCustomerRequestAuth(requestOptions, {
      getIdToken,
      forceRefresh: true,
    })
  );
}
